import type { FC } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import { Checklist } from '@eneco/components/ui/Checklist/Checklist';
import BrandBox from '@eneco/custom-components/BrandBox/BrandBox';
import { useLinkComponent } from '@eneco/link';
import {
  Stack,
  ButtonLink,
  Grid,
  Card,
  Box,
  Text,
  TextLink,
  Stretch,
  Bleed,
  Badge,
  PromotionalCard,
} from '@eneco/sparky';
import { useMediaQuery } from '@eneco/sparky/hooks';
import { BadgeProps, BoxProps, TextColors } from '@eneco/sparky/types';
import { wrap, Placeholder } from '@sitecore/common';
import { Fields } from '@sitecore/types/HeroCardRebranded';
import { CustomHeroCardRebrandedRendering } from '@sitecore/types/manual/HeroCardRebranded';

import { promotionalCardWrapClass } from './HeroCardRebranded.css';
import { toPromotionalCardTitle } from './toPromotionalCardTitle';

type FooterTypes = Pick<Fields, 'footerLink' | 'footerText'>;
type FooterStyles = {
  footerBackgroundColor?: BoxProps['backgroundColor'];
  footerTextColor: keyof TextColors;
};
type ContentBlockTypes = Pick<Fields, 'content' | 'buttonLink' | 'link' | 'secondaryButtonLink' | 'uspList'> &
  Partial<Pick<Fields, 'badgeText' | 'badgeColorOption'>>;
type Placeholders = Pick<CustomHeroCardRebrandedRendering, 'placeholders'>;

const isSitecoreCheckboxOn = (value?: string | boolean) => value === true || value === 'true' || value === '1';

const Footer: FC<FooterTypes & FooterStyles & { isDesktop: boolean; Link: ReturnType<typeof useLinkComponent> }> = ({
  footerLink,
  footerText,
  footerTextColor,
  footerBackgroundColor,
  isDesktop,
  Link,
}) => {
  if (
    !((footerLink?.value?.href || footerText?.value) && footerBackgroundColor && footerTextColor) &&
    !footerText?.editable
  ) {
    return null;
  }

  return (
    <Box
      backgroundColor={footerBackgroundColor}
      borderBottomLeftRadius={isDesktop ? 'm' : 's'}
      borderBottomRightRadius={isDesktop ? 'm' : 's'}
      borderTopLeftRadius={isDesktop ? undefined : 's'}
      borderTopRightRadius={isDesktop ? undefined : 's'}
      paddingX={{ initial: '6', md: '10' }}
      paddingY="2">
      <Stack alignX="center">
        <Stack.Item as="span">
          {footerText?.value || footerText?.editable ? (
            <Box as="span" paddingRight={footerLink.value.href || footerLink.editable ? 2 : 0}>
              <Text size="BodyS" display="inline" weight="bold" color={footerTextColor}>
                {wrap(footerText)}
              </Text>
            </Box>
          ) : null}
          {footerLink?.value?.href || footerLink?.editable ? (
            <Text size="BodyS" display="inline">
              <Link editable={footerLink.editable} linkType={footerLink.value.linktype} linkValue={footerLink.value}>
                <TextLink target={footerLink.value.target} color={footerTextColor}>
                  {footerLink.value.text}
                </TextLink>
              </Link>
            </Text>
          ) : null}
        </Stack.Item>
      </Stack>
    </Box>
  );
};

const ContentBlock: FC<ContentBlockTypes & Placeholders> = ({
  content,
  buttonLink,
  link,
  placeholders,
  secondaryButtonLink,
  uspList,
  badgeText,
  badgeColorOption,
}) => {
  const Link = useLinkComponent();
  const isDesktop = useMediaQuery('lg');

  return (
    <Box borderRadius="m" padding={{ initial: '0', lg: '8' }} paddingTop={{ initial: '6', lg: '12' }}>
      <Stack gap="6">
        {/* Placeholder for optional small components between the title and text */}
        {!!placeholders?.['jss-hero-card-top']?.length && (
          <Box>
            <Placeholder name="jss-hero-card-top" />
          </Box>
        )}
        {(badgeText?.value || badgeText?.editable) && (
          <Box>
            <Badge size={isDesktop ? 'M' : 'S'} color={badgeColorOption?.value as BadgeProps['color']}>
              {wrap(badgeText)}
            </Badge>
          </Box>
        )}
        {(content.value || content.editable) && (
          <Text size={{ initial: 'BodyM', lg: 'BodyL' }}>{wrap(content, <RichText html={content.value} />)}</Text>
        )}

        {uspList?.value?.enum?.length && (
          <Checklist>
            {uspList.value.enum.map(usp => {
              return (
                <Checklist.Item alignY="start" key={usp.name}>
                  {usp.label}
                </Checklist.Item>
              );
            })}
          </Checklist>
        )}
        {!!placeholders?.['jss-hero-card-main']?.length && (
          <Box>
            <Placeholder name="jss-hero-card-main" />
          </Box>
        )}
        {((buttonLink.value.href && buttonLink.value.text) || buttonLink.editable) && (
          <Stack direction="row" gap="3" wrap>
            <Stack.Item grow={false}>
              <Stretch width={{ initial: true, lg: false }}>
                <Link editable={buttonLink.editable} linkType={buttonLink.value.linktype} linkValue={buttonLink.value}>
                  <ButtonLink target={buttonLink.value.target}>{buttonLink.value.text}</ButtonLink>
                </Link>
              </Stretch>
            </Stack.Item>
            {((secondaryButtonLink.value.href && secondaryButtonLink.value.text) || secondaryButtonLink.editable) && (
              <Stack.Item grow={false}>
                <Stretch width={{ initial: true, lg: false }}>
                  <Link
                    editable={secondaryButtonLink.editable}
                    linkType={secondaryButtonLink.value.linktype}
                    linkValue={secondaryButtonLink.value}>
                    <ButtonLink action="secondary" target={secondaryButtonLink.value.target}>
                      {secondaryButtonLink.value.text}
                    </ButtonLink>
                  </Link>
                </Stretch>
              </Stack.Item>
            )}
          </Stack>
        )}
        {((link.value.href && link.value.text) || link.editable) && (
          <Stack.Item grow={false}>
            <Link editable={link.editable} linkType={link.value.linktype} linkValue={link.value}>
              <TextLink emphasis="high" target={link.value.target}>
                {link.value.text}
              </TextLink>
            </Link>
          </Stack.Item>
        )}
      </Stack>
    </Box>
  );
};

const HeroCardRebranded: FC<CustomHeroCardRebrandedRendering> = ({
  fields: {
    title,
    content,
    footerLink,
    footerText,
    buttonLink,
    link,
    secondaryButtonLink,
    uspList,
    badgeText,
    badgeColorOption,
  },
  placeholders,
  params,
}) => {
  const Link = useLinkComponent();
  const isDesktop = useMediaQuery('lg');
  const promotionalTitle = isSitecoreCheckboxOn(params.setWieDoetErMeeVariantFlag)
    ? toPromotionalCardTitle(title.value ?? '')
    : undefined;

  return (
    <Grid columns={{ initial: '1', lg: '12' }}>
      <Grid.Item gridColumn={{ initial: '1/-1', lg: '1/11' }} gridRow={{ lg: '1 / -1' }} order={{ lg: 2 }}>
        {(title.value || title.editable) &&
          (promotionalTitle ? (
            <Box className={promotionalCardWrapClass} paddingLeft={{ initial: '0', lg: '8' }}>
              <PromotionalCard title={promotionalTitle} headingAs="h1" background="brand" size="l">
                <Box />
              </PromotionalCard>
            </Box>
          ) : (
            <BrandBox
              title={title.value}
              borderRadius="all"
              padding={{ initial: '6', lg: '8' }}
              size={{ initial: 'S', lg: 'L' }}
              headingAs="h1"
            />
          ))}
      </Grid.Item>

      <Grid.Item gridColumn={{ initial: '1/-1', lg: '1/13' }}>
        <Bleed top={{ initial: '0', lg: '6' }}>
          <Box paddingLeft={{ initial: '0', lg: '8' }}>
            {isDesktop ? (
              <Card corners="rounded" overflow="hidden">
                <ContentBlock
                  content={content}
                  buttonLink={buttonLink}
                  link={link}
                  placeholders={placeholders}
                  secondaryButtonLink={secondaryButtonLink}
                  uspList={uspList}
                  badgeText={badgeText}
                  badgeColorOption={badgeColorOption}
                />
                <Footer footerLink={footerLink} footerText={footerText} {...params} isDesktop={isDesktop} Link={Link} />
              </Card>
            ) : (
              <Stack gap="4">
                <ContentBlock
                  content={content}
                  buttonLink={buttonLink}
                  link={link}
                  placeholders={placeholders}
                  secondaryButtonLink={secondaryButtonLink}
                  uspList={uspList}
                  badgeText={badgeText}
                  badgeColorOption={badgeColorOption}
                />
                <Footer footerLink={footerLink} footerText={footerText} {...params} isDesktop={isDesktop} Link={Link} />
              </Stack>
            )}
          </Box>
        </Bleed>
      </Grid.Item>
    </Grid>
  );
};

export { HeroCardRebranded, ContentBlock };
export default HeroCardRebranded;
